import React from 'react'
import {useTranslation} from 'react-i18next'
import {AdminCodeItem} from '../../domain/entities/AdminCode.ts'
import {useAdminCodeHistory} from '../../hooks/useAdminCodes.ts'
import {AdminCodeStepPill} from './AdminCodeStepPill.tsx'
import IconClose from '../../assets/icons/close.svg?react'

const formatDateTime = (iso: string): string => {
    const date = new Date(iso)
    if (isNaN(date.getTime())) return iso
    return new Intl.DateTimeFormat('es-ES', {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'}).format(date)
}

interface AdminCodeHistorySidePanelProps {
    row: AdminCodeItem
    onClose: () => void
}

// Slide-over changelog for one code — mirrors the structural pattern of
// agencyDetail/TripbookSidePanel.tsx (backdrop + right-hand panel) but reads
// straight from the admin history endpoint instead of an agency's own data.
export const AdminCodeHistorySidePanel: React.FC<AdminCodeHistorySidePanelProps> = ({row, onClose}) => {
    const {t} = useTranslation('dashboard')
    // El pedido de ESTA fila, no el del código: un genérico tiene varios y el
    // panel debe contar los hitos del que se ha abierto.
    const {entries, isLoadingHistory} = useAdminCodeHistory(row.codeId, row.order?.orderId ?? null)

    return (
        <div className="fixed inset-0 z-40">
            <div onClick={onClose} className="absolute inset-0 bg-neutralBlack-900/25"/>
            <aside className="absolute top-0 right-0 bottom-0 w-[520px] max-w-[92vw] bg-white border-l border-neutralBlack-200 shadow-2xl flex flex-col">
                <div className="flex items-start gap-3.5 px-6 pt-[22px] pb-[18px] border-b border-neutralBlack-200">
                    <div className="flex-1 min-w-0 flex flex-col gap-2">
                        <span className="flex items-center gap-2.5 flex-wrap">
                            <span className="text-[19px] font-semibold tracking-tight text-neutralBlack-900 tracking-wide">{row.code}</span>
                            <AdminCodeStepPill step={row.currentStep}/>
                        </span>
                        <span className="text-[13.5px] text-neutralBlack-500">
                            {(row.traveler.name ?? t('adminCodes.noTraveler')) + (row.agency.name ? ' · ' + row.agency.name : '')}
                        </span>
                    </div>
                    <button type="button" onClick={onClose}
                            className="w-8 h-8 flex-none flex items-center justify-center rounded-full bg-neutralBlack-100 text-neutralBlack-700 hover:bg-neutralBlack-200">
                        <IconClose className="size-3"/>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-[22px] pb-10 flex flex-col gap-4">
                    <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('adminCodes.historyTitle')}</span>

                    {isLoadingHistory ? (
                        <div className="flex flex-col gap-3">
                            {Array.from({length: 4}).map((_, i) => (
                                <div key={i} className="h-14 rounded-xl bg-neutralBlack-50 animate-pulse"/>
                            ))}
                        </div>
                    ) : entries.length === 0 ? (
                        <div className="flex flex-col items-center text-center py-10 px-4">
                            <p className="text-sm text-neutralBlack-500">{t('adminCodes.historyEmpty')}</p>
                        </div>
                    ) : (
                        <ol className="flex flex-col gap-0">
                            {entries.map((entry, i) => (
                                <li key={i} className="relative pl-5 pb-5 border-l border-neutralBlack-200 last:border-transparent last:pb-0">
                                    <span className="absolute -left-[4.5px] top-1 w-[9px] h-[9px] rounded-full bg-neutralBlack-900"/>
                                    <div className="flex flex-col gap-1">
                                        <span className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[13px] font-medium text-neutralBlack-900">{entry.tipo}</span>
                                            <span className="text-[11.5px] text-neutralBlack-400">{formatDateTime(entry.momento)}</span>
                                        </span>
                                        {entry.campo && (
                                            <span className="text-[13px] text-neutralBlack-700">
                                                <span className="font-medium text-neutralBlack-900">{entry.campo}</span>
                                                {': '}
                                                <span className="text-neutralBlack-500 line-through">{entry.antes ?? '—'}</span>
                                                {' → '}
                                                <span className="text-neutralBlack-900">{entry.despues ?? '—'}</span>
                                            </span>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            </aside>
        </div>
    )
}
